import { HELP_CENTER_URL, isNull } from '@aloft/toolkit-shared';
import { Block, Flex, IconButton, Typography } from '@aloft/toolkit-sui';

import { Link } from 'react-router-dom';

import { ContentfulDocument } from '../../ContentfulDocument/ContentfulDocument.tsx';
import { LoadingScreen } from '../../LoadingScreen/LoadingScreen.tsx';
import { PageID } from './types.ts';
import { useHelpDrawerDoc } from './useHelpDrawerDoc.ts';

type Props = {
  pageID: PageID | null;
  isPopout?: boolean;
};

const Layout = ({
  pageID,
  isPopout,
  children,
}: React.PropsWithChildren<{ pageID: Props['pageID']; isPopout: boolean }>) => {
  const showPopoutButton = !isPopout;

  return (
    <Flex
      flexDirection="column"
      backgroundColor="surface1"
      paddingX="24px"
      paddingY="12px"
      minHeight="100%"
      gap={4}
      position={showPopoutButton ? 'relative' : undefined}
      paddingTop={showPopoutButton ? '3rem' : undefined}
      className="help-drawer-layout"
    >
      <Typography variant="h2">Page Guide</Typography>
      {children}
      {showPopoutButton && (
        <Block position="absolute" top="8px" left="0">
          <IconButton
            icon="external-link"
            variant="tertiary"
            onClick={() => {
              window.open(
                `/page-guide/${pageID}`,
                'page-guide-popout',
                'height=700,width=700,left=100000', // left=100000 forces the window to open to the right
              );
            }}
            ariaLabel="Open Page Guide in a new window"
            iconSize="lg"
            size="sm"
          />
        </Block>
      )}
    </Flex>
  );
};

const FallbackContent = () => (
  <Typography>
    Please visit the&nbsp;
    <Link target="_blank" to={HELP_CENTER_URL}>
      <Typography variant="link" inline>
        Help Center
      </Typography>
    </Link>
    &nbsp;for additional assistance.
  </Typography>
);

export const HelpDrawer = ({ pageID, isPopout = false }: Props) => {
  const { document, isLoading, isError, error } = useHelpDrawerDoc({ pageID });

  let content: React.ReactNode;

  if (isNull(pageID)) {
    console.warn(`Invalid page ID: ${pageID} - Falling back to Help Center.`);
    content = <FallbackContent />;
  } else if (isLoading) {
    content = <LoadingScreen what="Page Guide" />;
  } else if (isError) {
    console.warn(
      `Error fetching help document for pageID: ${pageID} - Falling back to Help Center.`,
      error,
    );
    content = <FallbackContent />;
  } else if (isNull(document)) {
    console.warn(
      `No help document found for pageID: ${pageID}.`,
      'Ensure this content exists in the CMS.',
    );
    content = <FallbackContent />;
  } else {
    content = <ContentfulDocument document={document.fields.content} />;
  }

  return (
    <Layout pageID={pageID} isPopout={isPopout}>
      {content}
    </Layout>
  );
};
